import * as React from 'react';
import { Bell, Loader2 } from 'lucide-react';
import { format } from 'date-fns';
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';

import { notificationsApi, notificationKeys } from '../api';
import type { UserNotification } from '../types';

const PAGE_SIZE = 20;

export function Notifications() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(false);

  // Infinite list — the next page loads when the user scrolls to the bottom.
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: notificationKeys.list(),
    queryFn: ({ pageParam }) => notificationsApi.getAll(pageParam, PAGE_SIZE),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce(
        (sum, page) => sum + (page?.results?.length ?? 0),
        0,
      );
      return loaded < (lastPage?.meta?.total ?? 0) ? loaded : undefined;
    },
  });

  const items = React.useMemo(
    () => data?.pages?.flatMap((page) => page?.results ?? []) ?? [],
    [data],
  );

  const { data: count = 0 } = useQuery({
    queryKey: notificationKeys.count(),
    queryFn: notificationsApi.getCount,
  });

  const { mutate: markAsRead } = useMutation({
    mutationFn: (id: string | number) => notificationsApi.markAsRead(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.list() });
      queryClient.invalidateQueries({ queryKey: notificationKeys.count() });
    },
  });

  const { mutate: markAllAsRead, isPending: isMarkingAll } = useMutation({
    mutationFn: () => notificationsApi.markAllAsRead(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.list() });
      queryClient.invalidateQueries({ queryKey: notificationKeys.count() });
    },
  });

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 60;
    if (nearBottom && hasNextPage && !isFetchingNextPage) {
      void fetchNextPage();
    }
  };

  // Deep-link: notifications carry `data.taskId` / `data.contractId` — clicking
  // routes to the matching list page filtered by the exact id (same `?id=`
  // filtration the tasks page implements; no status filter applied).
  const handleClick = (item: UserNotification) => {
    if (item?.readedAt === null) {
      markAsRead(item.notification.id);
    }
    const payload = item?.notification?.data;
    if (payload?.taskId) {
      setOpen(false);
      navigate(`/app/new-tasks?id=${payload.taskId}`);
    } else if (payload?.contractId) {
      setOpen(false);
      navigate(`/app/contracts?id=${payload.contractId}`);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button className="relative rounded-full bg-top-navigation-bg text-white hover:bg-button-ground hover:text-black">
          <Bell className="h-4 w-4" />
          {count > 0 && (
            <div className="absolute -right-0 -top-0 flex h-4 w-4 items-center justify-center rounded-full bg-red-600 text-[10px] text-white">
              {count}
            </div>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="mr-20 w-96 p-0">
        <div className="flex items-center gap-2 border-b p-4">
          <Bell className="h-5 w-5 text-blue-600" />
          <span className="font-medium">Notifications</span>
          {count > 0 && (
            <Badge variant="secondary" className="bg-blue-100 text-blue-800">
              {count} new
            </Badge>
          )}
          {count > 0 && (
            <button
              type="button"
              onClick={() => markAllAsRead()}
              disabled={isMarkingAll}
              className="ml-auto text-xs font-medium text-blue-600 hover:underline disabled:opacity-50"
            >
              {t('readAll')}
            </button>
          )}
        </div>
        <div className="h-[300px] overflow-y-auto" onScroll={handleScroll}>
          <div className="flex flex-col gap-1 p-2">
            {items.length === 0 && (
              <p className="py-8 text-center text-sm text-muted-foreground">
                No notifications
              </p>
            )}
            {items.map((item) => (
              <div
                key={item.id}
                role="button"
                tabIndex={0}
                onClick={() => handleClick(item)}
                onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && handleClick(item)}
                className={`relative cursor-pointer rounded-lg border p-3 transition-colors hover:bg-blue-50 ${
                  item.readedAt === null ? 'bg-blue-100 border-blue-200' : 'bg-background'
                }`}
              >
                <h4 className="pr-20 text-sm font-medium leading-tight">
                  {item.notification.title}
                </h4>
                <p className="mt-1 text-sm text-muted-foreground">
                  {item.notification.message}
                </p>
                <span className="absolute right-3 top-2 text-[11px] text-muted-foreground">
                  {format(new Date(item.createdAt), 'MM/dd/yyyy HH:mm')}
                </span>
              </div>
            ))}
            {isFetchingNextPage && (
              <div className="flex justify-center py-2">
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              </div>
            )}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
